import type { SecurityTypes } from '@talos/kyoko';
import type { OptionFilter } from './Filter/types';
import { getOptionsAfterFilter } from './hooks/useOptionChainData';

export interface OptionBalancesContext {
  /** Currency passed to BALANCE_DELTA when loading margin/equity figures for the selected option bucket. */
  balanceCurrency: string;
  /** Underlying spot symbol used for the ref-rate and sparkline requests in the balances strip. */
  spotSymbol: string;
}

/**
 * Resolves the balances request currency and underlying market-data symbol for the current options panel.
 * This stays panel-local because settlement markets can put several settlement variants under one option coin.
 */
export function resolveOptionBalancesContext(
  optionFilter: OptionFilter,
  options: SecurityTypes['Option'][]
): OptionBalancesContext {
  const matchingOptions: SecurityTypes['Option'][] = getOptionsAfterFilter(options, {
    currency: optionFilter.currency,
    market: optionFilter.market,
    underlyingCode: optionFilter.underlyingCode,
    settlementType: optionFilter.settlementType,
    settlementCurrency: optionFilter.settlementCurrency,
  });
  const representativeOption = matchingOptions.at(0);

  return {
    // Prefer explicit panel state because settlement variants under one option coin can settle in different currencies.
    balanceCurrency:
      optionFilter.settlementCurrency ?? representativeOption?.SettlementCurrency ?? optionFilter.currency,
    // Market data is about the underlying, not the settlement variant; fall back to the conventional spot pair.
    spotSymbol:
      representativeOption?.UnderlyingSymbol ??
      `${optionFilter.currency}-${representativeOption?.UnderlyingQuoteCurrency ?? 'USD'}`,
  };
}
